import { SoftSurveyComplete } from '@chiroup/components/survey/soft';
import { RequestStep } from '@chiroup/core/types/Request.type';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/24/solid';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RequestBootstrap } from '../../services/request.service';
import StepBasicInformation from './StepBasicInformation';
import StepConsent from './StepConsent';
import StepCustomForm from './StepCustomForm';
import StepInsurance from './StepInsurance';
import StepSurvey from './StepSurvey';
import StepUpload from './StepUpload';

const verbiage = {
  en: {
    of: 'of',
    step: 'Step',
    task: 'Task',
    allDone: 'All set',
    allDoneBody: 'You’ve completed everything we needed before your visit. Thank you.',
    close: 'Close',
    unknownTitle: 'Unsupported step',
    unknownBody: 'This step type isn’t recognized.',
    skip: 'Skip',
    backToMenu: 'Back to menu',
  },
  es: {
    of: 'de',
    step: 'Paso',
    task: 'Tarea',
    allDone: '¡Listo!',
    allDoneBody: 'Ha completado todo lo que necesitábamos antes de su visita. Gracias.',
    close: 'Cerrar',
    unknownTitle: 'Paso no compatible',
    unknownBody: 'Este tipo de paso no es reconocido.',
    skip: 'Saltar',
    backToMenu: 'Volver al menú',
  },
};

// Kiosk: how long the per-task completion checkmark shows before advancing.
const KIOSK_TASK_ADVANCE_MS = 4500;

type Props = {
  bootstrap: RequestBootstrap;
  primaryColor?: string;
  kiosk?: boolean;
  // When true, the runner is being driven step-by-step from a parent
  // menu (currently just the kiosk). On completion of the lone step
  // we exit straight back to the parent instead of rendering the
  // runner's "All set" terminal panel — the parent decides what to
  // show next (the menu, with this step now marked complete).
  singleStepMode?: boolean;
  // Kiosk single-step mode only: `lastStep` marks this as the patient's
  // final outstanding step, and `onFinishAll` returns the kiosk to its
  // login screen. The survey completion panel uses these to redirect
  // straight to login (rather than back to the menu) once everything's
  // done.
  lastStep?: boolean;
  onFinishAll?: () => void;
  // Kiosk sequential: fired when every step is complete (parent shows its
  // own "checked in" terminal instead of the runner's "All set" panel).
  onAllComplete?: () => void;
  onUpdateRequest: (data: Partial<RequestBootstrap>) => void;
  onExit: () => void;
  // Clinic Survey Preferences "Do not allow unrelated regions" —
  // forwarded to the CCS body picker via StepSurvey.
  doNotAllowUnrelatedRegions?: boolean;
};

const RequestRunner: React.FC<Props> = ({
  bootstrap,
  primaryColor = '#77bd43',
  kiosk,
  singleStepMode,
  lastStep,
  onFinishAll,
  onAllComplete,
  onUpdateRequest,
  onExit,
  doNotAllowUnrelatedRegions,
}) => {
  const language: 'en' | 'es' = bootstrap.language === 'es' ? 'es' : 'en';
  const v = verbiage[language];
  const validationCode = bootstrap.validationCode ?? '';

  // Kiosk runs all steps in one mounted runner; singleStepMode feeds one
  // at a time from a menu.
  const kioskSequential = !!kiosk && !singleStepMode;

  // Sort steps and filter out anything kiosk should hide (legacy
  // behavior: kiosk skips Photo ID and Insurance card uploads).
  const orderedSteps = useMemo(() => {
    const sorted = [...bootstrap.steps].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    if (!kiosk) return sorted;

    return sorted.filter(s => s.type !== 'uploadPhotoId' && s.type !== 'uploadInsuranceCard');
  }, [bootstrap.steps, kiosk]);

  const firstIncomplete = orderedSteps.findIndex(s => !s.complete && !s.disabled);
  const [activeIndex, setActiveIndex] = useState<number>(
    firstIncomplete === -1 ? orderedSteps.length : firstIncomplete
  );

  const allDone = activeIndex >= orderedSteps.length;
  const activeStep: RequestStep | undefined = orderedSteps[activeIndex];

  // Kiosk: the just-finished task, shown briefly before auto-advancing.
  const [completing, setCompleting] = useState<{ title: string; remaining: number } | null>(null);
  const completingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (completingTimerRef.current) clearTimeout(completingTimerRef.current);
    },
    []
  );

  // Single-step mode (kiosk picks one item from the menu). When the
  // step's own complete panel calls `completeStep`, we end up
  // `allDone` — fire `onExit` so control returns to the menu, where
  // the just-finished step now reads as Completed and the patient
  // can pick the next thing to work on. Skip the runner's own
  // "All set" terminal panel entirely; it'd be a misleading
  // "You've completed everything" when the patient may have only
  // done one of several items.
  useEffect(() => {
    if (allDone && singleStepMode) {
      onExit();
    }
  }, [allDone, singleStepMode, onExit]);

  // Kiosk sequential: hand off to the parent's terminal when all steps done.
  useEffect(() => {
    if (allDone && kioskSequential && onAllComplete) {
      onAllComplete();
    }
  }, [allDone, kioskSequential, onAllComplete]);

  // Refs so the deferred completeStep callback (fired after a
  // whole-survey submit) reads post-merge state instead of the stale
  // closure. Mirrors `stepsRef` in legacy Request.tsx:92.
  const orderedStepsRef = useRef(orderedSteps);
  const activeIndexRef = useRef(activeIndex);
  useEffect(() => {
    orderedStepsRef.current = orderedSteps;
  }, [orderedSteps]);
  useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);

  const completeStep = useCallback(() => {
    const currentSteps = orderedStepsRef.current;
    const currentIndex = activeIndexRef.current;
    const currentActive = currentSteps[currentIndex];

    if (!currentActive) return;

    // Skip the FE-side complete write when the backend already marked
    // it; otherwise we race onUpdateRequest and can clobber queued
    // followups.
    const updatedSteps = currentActive.complete
      ? currentSteps
      : currentSteps.map((s, i) => (i === currentIndex ? { ...s, complete: true } : s));

    if (updatedSteps !== currentSteps) {
      onUpdateRequest({ steps: updatedSteps });
    }

    // Kiosk: advance to the FIRST incomplete step. Follow-ups appended
    // mid-flow can sort ahead of the current index; a forward-only walk
    // would strand them. Other modes keep the index walk.
    const nextIndex = kioskSequential
      ? updatedSteps.findIndex(s => !s.complete && !s.disabled)
      : updatedSteps.findIndex((s, i) => i > currentIndex && !s.complete && !s.disabled);

    // Kiosk: show the shared "[task] complete! X more to go" checkmark for
    // every step type, then advance. The final task skips it (→ terminal).
    if (kioskSequential && nextIndex !== -1) {
      const remaining = updatedSteps.filter(s => !s.complete && !s.disabled).length;
      const title =
        (language === 'es' && currentActive.esTitle
          ? currentActive.esTitle
          : currentActive.title) ||
        currentActive.survey?.title ||
        currentActive.consent?.title ||
        '';

      setCompleting({ title, remaining });
      if (completingTimerRef.current) clearTimeout(completingTimerRef.current);
      completingTimerRef.current = setTimeout(() => {
        setCompleting(null);
        // Fresh refs — a submit may have appended follow-ups during the pause.
        const steps = orderedStepsRef.current;
        const next = steps.findIndex(s => !s.complete && !s.disabled);
        setActiveIndex(next === -1 ? steps.length : next);
      }, KIOSK_TASK_ADVANCE_MS);

      return;
    }

    setActiveIndex(nextIndex === -1 ? updatedSteps.length : nextIndex);
  }, [onUpdateRequest, kioskSequential, language]);

  // Bridges submitStuff's `(data, callback)` shape onto onUpdateRequest.
  // The setTimeout defer lets React flush the bootstrap update before
  // the callback runs (so completeStep sees fresh steps).
  const updateRequest = useCallback(
    (data: Partial<RequestBootstrap>, callback?: () => void) => {
      onUpdateRequest(data);
      if (callback) setTimeout(callback, 0);
    },
    [onUpdateRequest]
  );

  const visibleStepCount = orderedSteps.filter(s => !s.disabled).length;
  const completedStepCount = orderedSteps.filter(s => !s.disabled && s.complete).length;
  // Kiosk position from the completed count (robust to re-ordered follow-ups).
  const currentDisplayIndex = kioskSequential
    ? completedStepCount + 1
    : orderedSteps.slice(0, activeIndex).filter(s => !s.disabled).length + 1;
  // Kiosk reads "Task 1 of 2"; the public/step flow keeps "Step 1 of 2".
  const stepWord = kioskSequential ? v.task : v.step;

  const renderStep = () => {
    if (!activeStep) return null;

    const stepTitle =
      language === 'es' && activeStep.esTitle ? activeStep.esTitle : activeStep.title;
    const stepDescription =
      language === 'es' && activeStep.esDescription
        ? activeStep.esDescription
        : activeStep.description;

    if (activeStep.type === 'survey' && activeStep.patientSurveyId && activeStep.survey) {
      return (
        <StepSurvey
          key={activeStep.patientSurveyId}
          requestId={bootstrap.id}
          validationCode={validationCode}
          patientSurveyId={activeStep.patientSurveyId}
          survey={{
            id: activeStep.survey.id,
            title: stepTitle || activeStep.survey.title,
            subtitle: stepDescription,
            startMessage: activeStep.survey.startMessage,
            endMessage: activeStep.survey.endMessage,
            questions: activeStep.survey.questions,
            // Bootstrap-supplied resume data — the legacy
            // `getStepsToSend` strips `questions` for non-custom-
            // behavesAs surveys and instead pre-computes these so
            // the runner doesn't have to walk the entire question
            // list client-side.
            currentQuestion: activeStep.survey.currentQuestion,
            previousQuestion: activeStep.survey.previousQuestion,
            progress: activeStep.survey.progress,
            started: activeStep.survey.started,
            draft: activeStep.survey.draft,
            behavesAs: activeStep.survey.behavesAs,
            allowAdditionalComplaints: activeStep.survey.allowAdditionalComplaints,
          }}
          language={language}
          primaryColor={primaryColor}
          prMap={bootstrap.prMap}
          encounterId={bootstrap.encounterId}
          kiosk={kiosk}
          lastStep={lastStep}
          onFinishAll={onFinishAll}
          alreadyComplete={!!activeStep.complete}
          onComplete={completeStep}
          gender={bootstrap.gender}
          age={bootstrap.age}
          ccsFollowups={bootstrap.ccsFollowups}
          complaints={bootstrap.complaints}
          doNotAllowUnrelatedRegions={doNotAllowUnrelatedRegions}
          updateRequest={updateRequest}
        />
      );
    }

    if (activeStep.type === 'form' && activeStep.patientFormId && activeStep.form) {
      return (
        <StepCustomForm
          requestId={bootstrap.id}
          validationCode={validationCode}
          patientFormId={activeStep.patientFormId}
          form={activeStep.form}
          basePdfUrl={activeStep.basePdfUrl}
          primaryColor={primaryColor}
          prMap={bootstrap.prMap}
          alreadyComplete={!!activeStep.complete}
          kiosk={kiosk}
          onComplete={completeStep}
        />
      );
    }

    if (activeStep.type === 'consent' && activeStep.patientConsentId) {
      return (
        <StepConsent
          requestId={bootstrap.id}
          validationCode={validationCode}
          patientConsentId={activeStep.patientConsentId}
          consent={{
            title: stepTitle || activeStep.consent?.title || '',
            url: activeStep.consent?.url || null,
          }}
          language={language}
          primaryColor={primaryColor}
          prMap={bootstrap.prMap}
          alreadyComplete={!!activeStep.complete}
          kiosk={kiosk}
          onComplete={completeStep}
        />
      );
    }

    if (activeStep.type === 'uploadPhotoId') {
      return (
        <StepUpload
          kind="photoId"
          requestId={bootstrap.id}
          validationCode={validationCode}
          language={language}
          primaryColor={primaryColor}
          prMap={bootstrap.prMap}
          alreadyComplete={!!activeStep.complete}
          onComplete={completeStep}
        />
      );
    }

    if (activeStep.type === 'uploadInsuranceCard') {
      return (
        <StepUpload
          kind="insuranceCard"
          requestId={bootstrap.id}
          validationCode={validationCode}
          language={language}
          primaryColor={primaryColor}
          prMap={bootstrap.prMap}
          alreadyComplete={!!activeStep.complete}
          onComplete={completeStep}
        />
      );
    }

    if (activeStep.type === 'insurance') {
      return (
        <StepInsurance
          requestId={bootstrap.id}
          validationCode={validationCode}
          language={language}
          primaryColor={primaryColor}
          alreadyComplete={!!activeStep.complete}
          kiosk={kiosk}
          prMap={bootstrap.prMap}
          clinicId={bootstrap.clinicId}
          step={activeStep}
          onComplete={completeStep}
        />
      );
    }

    if (activeStep.type === 'basicInformation') {
      return (
        <StepBasicInformation
          requestId={bootstrap.id}
          validationCode={validationCode}
          language={language}
          primaryColor={primaryColor}
          prMap={bootstrap.prMap}
          alreadyComplete={!!activeStep.complete}
          kiosk={kiosk}
          clinicId={bootstrap.clinicId}
          age={bootstrap.age}
          smsConsent={bootstrap.smsConsent}
          smsConsentMarketing={bootstrap.smsConsentMarketing}
          step={activeStep}
          onComplete={completeStep}
        />
      );
    }

    return (
      <div className="portal-card p-6">
        <p className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1">
          {v.unknownTitle}
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400">{v.unknownBody}</p>
        <button
          type="button"
          onClick={completeStep}
          className="mt-4 inline-flex items-center justify-center rounded-full px-5 py-2 text-sm font-semibold text-white"
          style={{ backgroundColor: primaryColor }}
        >
          {v.skip}
        </button>
      </div>
    );
  };

  const showInlineHeader = !completing && (singleStepMode || (!allDone && visibleStepCount > 1));

  return (
    <div className="min-h-dvh flex flex-col antialiased">
      <main className="flex-1 px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className={`mx-auto ${kiosk ? 'max-w-3xl' : 'max-w-2xl'} flex flex-col gap-4`}>
          {/* Lightweight inline header — replaces the old sticky
              top bar. Back-to-menu link on the left (single-step
              mode only — the sequential flow has no list to back
              out to), step indicator on the right. No logo, no
              progress bar; the page IS the form. */}
          {showInlineHeader && (
            <div className="flex items-center justify-between gap-3">
              {singleStepMode ? (
                <button
                  type="button"
                  onClick={onExit}
                  className="inline-flex items-center gap-1 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 -ml-1 px-1 py-1 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[--ring]"
                  style={{ ['--ring' as any]: primaryColor } as React.CSSProperties}
                >
                  <ChevronLeftIcon className="size-4" />
                  {v.backToMenu}
                </button>
              ) : (
                <span />
              )}
              {!allDone && visibleStepCount > 1 && (
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 tabular-nums">
                  {stepWord} {currentDisplayIndex} {v.of} {visibleStepCount}
                </p>
              )}
            </div>
          )}

          {completing ? (
            // Kiosk: per-task completion checkmark shown between tasks.
            <div
              className="flex flex-col items-center gap-3 overflow-hidden rounded-3xl p-8 text-center dark:ring-1 dark:ring-white/10"
              style={{ backgroundColor: `${primaryColor}12` }}
            >
              <div
                className="flex size-14 items-center justify-center rounded-full"
                style={{ backgroundColor: primaryColor, color: '#fff' }}
                aria-hidden="true"
              >
                <CheckIcon className="size-8" />
              </div>
              <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
                {language === 'es'
                  ? `¡${completing.title} completado!`
                  : `${completing.title} complete!`}
              </h1>
              {completing.remaining > 0 && (
                <p className="text-base text-gray-600 dark:text-gray-300" aria-live="polite">
                  {language === 'es'
                    ? `Le queda${completing.remaining === 1 ? '' : 'n'} ${completing.remaining} tarea${completing.remaining === 1 ? '' : 's'} más.`
                    : `You have ${completing.remaining} more task${completing.remaining === 1 ? '' : 's'} to go.`}
                </p>
              )}
            </div>
          ) : allDone ? (
            // Single-step exits to the menu; kiosk hands off to its terminal.
            singleStepMode || kioskSequential ? null : (
              <SoftSurveyComplete
                language={language}
                primaryColor={primaryColor}
                title={v.allDone}
                message={v.allDoneBody}
                onExit={onExit}
                exitLabel={v.close}
              />
            )
          ) : (
            renderStep()
          )}
        </div>
      </main>
    </div>
  );
};

export default RequestRunner;
